import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { minerApi } from 'api';
import { RootState } from 'store';
import { GetMinerCalculations, Miner, MinerData } from 'types/api/api-miners.types';

export type MinersSliceType = {
  // The most recently fetched PAGE of devices (not the whole fleet).
  allMiners: Miner[] | null;
  calculationsByMinerId: Record<string, GetMinerCalculations.Calculation[]>;
  // Fleet-wide, from the API — independent of which page is loaded.
  activeMinerCount: number;
  deadMinersCount: number;
  totalMinerCount: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
};

export const initialState: MinersSliceType = {
  allMiners: null,
  calculationsByMinerId: {},
  activeMinerCount: 0,
  deadMinersCount: 0,
  totalMinerCount: 0,
  page: 1,
  pageSize: 50,
  isLoading: false,
};

const slice = createSlice({
  name: 'miners',
  initialState,
  reducers: {
    setMinersData: (state, action: PayloadAction<MinerData>) => {
      // The API returns the Miner→Device-renamed, paged shape ({ devices, activeDeviceCount,
      // deadDevicesCount, totalCount, page, pageSize }); fall back to the legacy names so
      // either wire shape populates.
      const payload = action.payload;
      const devices = payload.devices ?? payload.miners ?? [];
      state.allMiners = devices;
      state.activeMinerCount = payload.activeDeviceCount ?? payload.activeMinerCount ?? 0;
      state.deadMinersCount = payload.deadDevicesCount ?? payload.deadMinersCount ?? 0;
      state.totalMinerCount = payload.totalCount ?? devices.length;
      state.page = payload.page ?? 1;
      state.pageSize = payload.pageSize ?? devices.length;
    },
    setMinerCalculations: (
      state,
      action: PayloadAction<{ minerId: string; calculations: GetMinerCalculations.Calculation[] }>,
    ) => {
      state.calculationsByMinerId[action.payload.minerId] = action.payload.calculations;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(minerApi.endpoints.updateMinerData.matchFulfilled, (state, action) => {
      const arg = action.meta.arg.originalArgs;
      const { minerId, name } = arg;

      if (state.allMiners) {
        const minerIndex = state.allMiners.findIndex(m => m.id === minerId);
        if (minerIndex !== -1) {
          state.allMiners[minerIndex].name = name;
        }
      }
    });
  },
});

export default slice.reducer;

// basic selectors
export const minersSelector = (state: RootState) => state.miners;
export const allMinersSelector = (state: RootState) => state.miners.allMiners;
export const activeMinersCountSelector = (state: RootState) => state.miners.activeMinerCount;
export const deadMinersCountSelector = (state: RootState) => state.miners.deadMinersCount;
export const totalMinersCountSelector = (state: RootState) => state.miners.totalMinerCount;

// memoized selector for calculations
export const minerCalculationsSelector = (minerId: string) =>
  createSelector(
    (state: RootState) => state.miners.calculationsByMinerId,
    calculationsByMinerId => calculationsByMinerId[minerId] ?? [],
  );

export const { setMinersData, setMinerCalculations } = slice.actions;
